/**
 * Manual de uso in-app — guía paso a paso por feature.
 *
 * Versión React resumida (los capítulos clave). El mockup vanilla tenía
 * 14 capítulos colapsables muy detallados — en producción este contenido
 * se podría servir desde un CMS o markdown estático.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Capitulo {
  id: string;
  titulo: string;
  contenido: React.ReactNode;
}

const CAPITULOS: Capitulo[] = [
  {
    id: 'intro',
    titulo: '1. Introducción',
    contenido: (
      <div className="space-y-2">
        <p>
          Riga Traza es el sistema de trazabilidad farmacéutica de Everex Argentina S.A.
          Cumple con los requerimientos de ANMAT y su Sistema Nacional de Trazabilidad (SNT)
          para informar el movimiento de cada unidad de medicamento serializado.
        </p>
        <p>
          Esta versión React (Fase 3 en curso) está portada del mockup vanilla que vive en{' '}
          <code className="font-mono">/mockup/</code> del repositorio. Mantiene el mismo
          modelo de datos, lógica regulatoria y RBAC.
        </p>
      </div>
    ),
  },
  {
    id: 'login',
    titulo: '2. Login y multi-GLN',
    contenido: (
      <div className="space-y-2">
        <p>
          <b>Usuarios demo</b>: christian (Admin · Everex), laura (Operador ·
          Pharmatrans), pablo (Operador multi-GLN · Pharmatrans + Everex).
        </p>
        <p>
          <b>Multi-GLN</b>: si tu usuario tiene 2+ GLNs operables, el switcher del header
          te permite cambiar — requiere re-ingresar tu contraseña (step-up auth · 21 CFR
          Part 11 §11.10(d)). El cambio queda registrado en el audit.
        </p>
        <p>
          Los GLNs externos (laboratorios extranjeros / droguerías que nos envían producto)
          NUNCA aparecen en el switcher — son orígenes mock de transferencias entrantes.
        </p>
      </div>
    ),
  },
  {
    id: 'despacho',
    titulo: '3. Despacho',
    contenido: (
      <div className="space-y-2">
        <p>
          <b>Flujo</b>: Sidebar → Despacho → Click en un remito pendiente → Pickeo (scanner
          input + progreso por item) → Seleccionar evento ANMAT → Confirmar despacho →
          Firma electrónica → Toast de éxito.
        </p>
        <p>
          El SNT responde simulado en ~2s (90% éxito / 10% error tipificado). La tab
          "Completadas" muestra el estado actualizado automáticamente cada 3s.
        </p>
        <p>
          <b>Override del evento ANMAT</b>: si cambiás el evento default sugerido, la firma
          va a exigir razón obligatoria.
        </p>
        <p>
          <b>Cancelar transacción</b>: estados En cola / Informada / Error son cancelables.
          Estado Confirmada queda bloqueada con 🔒 (la contraparte ya confirmó la recepción).
        </p>
      </div>
    ),
  },
  {
    id: 'recepcion',
    titulo: '4. Recepción',
    contenido: (
      <div className="space-y-2">
        <p>
          Lista las transferencias entrantes hacia tu GLN activo, ya informadas al SNT por
          el origen. Click en una → Confirmar recepción → Firma electrónica.
        </p>
        <p>
          Las transferencias desde GLNs externos vienen marcadas con 🌐 (badge naranja).
        </p>
        <p>
          Al confirmar, las series pasan de en_transito → en_poder en tu GLN y se propaga
          la confirmación al despacho origen (CR-4 del audit GAMP 5).
        </p>
        <p>
          <b>Cancelar recepción</b>: solo si ninguna serie tiene movimientos posteriores.
          Revierte las series a en_transito en el GLN origen y reabre el despacho como pendiente.
        </p>
      </div>
    ),
  },
  {
    id: 'ingreso',
    titulo: '5. Ingreso / Alta manual + Lotes',
    contenido: (
      <div className="space-y-2">
        <p>
          Carga inicial de series para un GTIN+Lote en tu ubicación activa. Las series
          quedan registradas bajo tu tenencia pero <b>NO se informan al SNT</b> hasta que se despachen.
        </p>
        <p>
          La vista Lotes muestra todos los ingresos agrupados por <code>ingresoId</code>.
          Los lotes "Disponibles" (sin series usadas) pueden eliminarse con razón obligatoria.
          Si una serie ya fue despachada o pickeada, el lote queda "Parcial" o "Agotado" y
          no se puede borrar.
        </p>
      </div>
    ),
  },
  {
    id: 'audit',
    titulo: '6. Audit Trail + API Log (Admin)',
    contenido: (
      <div className="space-y-2">
        <p>
          Solo visibles para Administrador. El <b>Audit Trail</b> registra toda acción
          crítica con hash chain FNV-1a encadenado. Si alguien manipula el storage local,
          el banner rojo te avisa.
        </p>
        <p>
          El <b>Logs API</b> registra cada llamada saliente al ERP y al SNT (envío + callback
          de respuesta + cancelaciones), con KPIs, filtros por sistema/método/estado y modal
          de detalle JSON. Cap LRU a 500 entradas.
        </p>
        <p>
          Ambas vistas tienen export CSV para auditorías externas.
        </p>
      </div>
    ),
  },
  {
    id: 'deuda',
    titulo: '7. Deuda regulatoria para producción',
    contenido: (
      <div className="space-y-2">
        <p>
          La auditoría GAMP 5 / 21 CFR Part 11 documentó 14 puntos pendientes para producción.
          La lista completa con norma, estado y solución vive en la vista <b>Handoff técnico</b>.
        </p>
        <p>
          Resumen: hash chain criptográfico, firma electrónica con SSO, timestamps
          server-side, backup, schema migration no destructiva, lockout, timeout de sesión,
          URS/FS/DS versionados, eliminación del botón "Resetear demo" en builds productivos.
        </p>
      </div>
    ),
  },
];

export function ManualPage() {
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set(['intro']));

  const toggle = (id: string) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            <BookOpen className="h-5 w-5" />
            Manual de uso
          </CardTitle>
          <p className="text-xs text-gray-600">
            Guía paso a paso para Operadores y Administradores. Click en cada capítulo
            para expandir.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {CAPITULOS.map((cap) => {
            const expandido = expandidos.has(cap.id);
            return (
              <div key={cap.id} className="rounded border border-gray-200">
                <button
                  type="button"
                  onClick={() => toggle(cap.id)}
                  className={cn(
                    'flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-gray-50',
                    expandido && 'bg-gray-50',
                  )}
                >
                  <span className="font-semibold text-everex-azul">{cap.titulo}</span>
                  {expandido ? (
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  )}
                </button>
                {expandido && (
                  <div className="border-t border-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-700">
                    {cap.contenido}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
